/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import {
  getFeeLineItems,
  splitFeeLineItemsForReceipt,
} from '../utils/feeLineItems'
import { Spin } from 'antd'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CreditCardInput from 'src/components/CreditCardInput'
import {
  paragraphMedium,
  paragraphSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import Icon from 'src/components/Icon'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import usePaymentProvider, {
  PAYMENT_PROVIDERS,
  PAYMENT_STATUS,
} from 'src/hooks/usePaymentProvider'
import useRegistrationPaymentWebSocket, {
  CONFIRMATION_STATE,
} from 'src/hooks/useRegistrationPaymentWebSocket'
import { registerError } from 'src/utils/sentry'
import LoggedUserBar from '../components/LoggedUserBar'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import PaymentPlanSelector from '../components/PaymentPlanSelector'
import { RegistrationContext } from '../context/RegistrationContext'
import {
  PAYMENT_PLANS,
  getPaymentPlanOptions,
  isNativeOneTimePayment,
} from '../utils/paymentPlan'
import {
  PageTitle,
  REGISTRATION_STEPS,
  alternateButtonStyle,
  headingButtonStyle,
  headingSixStyle,
  paragraphMediumBoldStyle,
  paragraphMediumStyle,
  paragraphSmallBoldStyle,
} from './Main'

/**
 * Money column treatment. `tabular-nums` gives every digit the same advance
 * width so decimal points line up down the receipt; without it the amounts
 * read as a ragged column.
 */
const tabularAmountStyle = css`
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`

/**
 * Receipt line item (label and amount). Weight 500 — heavier than the shared
 * paragraphMediumStyle used elsewhere in the flow, which is regular.
 */
const receiptLineStyle = css`
  ${paragraphMedium}
  ${weightMedium}
  color: ${colors.WHITE};
`

/**
 * Secondary receipt line (fee rows and Tax) — 14px against receiptLineStyle's
 * 16px. These are adjustments to the Subtotal rather than items in their own
 * right, so they step down a size; color and weight stay identical so the
 * step is the only distinction and emphasis still belongs to the Total.
 */
const receiptSecondaryLineStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
`

/** Muted subtitle under a line item, e.g. the competition name. */
const receiptSubtitleStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
`

/**
 * RegistrationPayment - Handles the payment step of registration flow.
 * Renders payment info summary and supports Stripe, SportsPay, or in-person payment methods.
 * Uses WebSocket for real-time Stripe payment confirmation.
 * Side effects: API calls for payment processing, navigation to review step on success.
 */
const RegistrationPayment = () => {
  const {
    session,
    setStep,
    setSession,
    registration,
    deleteSession,
    currencyCode,
    couponInfo,
    division,
    role,
    teamOfficialType,
    isTeamRep,
    updateSessionConfirmSubmission,
    updateSessionConfirmSportsPaySubmission,
    updateSessionPayment,
    countries,
    email,
    resetSession,
  } = useContext(RegistrationContext)

  const [
    submittingAlreadyInPersonPayment,
    setSubmittingAlreadyInPersonPayment,
  ] = useState(false)

  const ACTIVE_PAYMENT_PROVIDER =
    registration?.registration?.payment_provider_type?.id

  const waiverRequired = registration?.registration?.show_waiver

  // Local state for error handling
  const [localError, setLocalError] = useState(null)
  const [alreadyPaidInPerson, setAlreadyPaidInPerson] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [hasSubmittedPayment, setHasSubmittedPayment] = useState(false)
  const [isInSuccessDelay, setIsInSuccessDelay] = useState(false)

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    cardType: '',
    errors: {},
  })

  // Ref to track if element has been mounted
  const elementMounted = useRef(false)

  const {
    total,
    tax,
    subtotal,
    discount,
    client_secret,
    // This is used by sports pay to dinamically load the script
    host_name,
    root_schedule_name,
    connected_account_id,
  } = session

  // Registrant-facing fee rows (League/Payment Processing Fee). Already
  // filtered server-side for this registrant — rendered verbatim.
  const feeLineItems = getFeeLineItems(session)
  const { taxedFees, postTaxFees } = splitFeeLineItemsForReceipt(feeLineItems)

  // WebSocket hook for real-time payment confirmation (Stripe only)
  const {
    confirmationState,
    confirmedSession,
    errorMessage: wsErrorMessage,
    startWaitingForConfirmation,
    resetConfirmation,
    isWaiting: isWaitingForWsConfirmation,
  } = useRegistrationPaymentWebSocket(session?.id)

  /**
   * Whether the division natively requires a single full payment (true) or uses
   * a deposit-first / installment flow (false). Shares the backend rule
   * (`(installments ?? 1) === 1 && single_payment`, player_* fields for players)
   * via the paymentPlan helper — single_payment alone is NOT enough: with
   * installments > 1 the division still prices a deposit flow.
   */
  const isSinglePayment = useMemo(
    () => isNativeOneTimePayment({ isTeamRep, division }),
    [isTeamRep, division]
  )

  /**
   * The deposit amount relevant to the current registrant type.
   * Team reps use `division.deposit`; players use `division.player_deposit`.
   */
  const effectiveDeposit = useMemo(() => {
    return isTeamRep ? division?.deposit : division?.player_deposit
  }, [isTeamRep, division?.deposit, division?.player_deposit])

  const deposit = useMemo(() => {
    if (couponInfo?.id) {
      return formatCurrency(effectiveDeposit, currencyCode)
    }
    return formatCurrency(subtotal, currencyCode)
  }, [couponInfo?.id, subtotal, currencyCode, effectiveDeposit])

  /**
   * Deposit Only vs Pay in Full. Options are null on native one-time divisions
   * (where the plan does not apply) — the server ignores the plan there too.
   * The server echo (session.payment_plan) is the source of truth for selection.
   */
  const paymentPlanOptions = useMemo(
    () => getPaymentPlanOptions({ isTeamRep, division }),
    [isTeamRep, division]
  )
  const selectedPaymentPlan =
    session?.payment_plan || PAYMENT_PLANS.DEPOSIT_ONLY
  const isPayInFullSelected =
    !!paymentPlanOptions && selectedPaymentPlan === PAYMENT_PLANS.PAY_IN_FULL
  const [isRepricing, setIsRepricing] = useState(false)

  /**
   * Nothing owed right now (e.g. a 100% coupon on the selected plan): no payment
   * element mounts and submission confirms the registration directly.
   */
  const hasNoBalanceDue =
    total !== null && total !== undefined && Number(total) <= 0

  /**
   * Handles the payment success flow with the following steps:
   * 1. For Stripe: Wait for WebSocket payment-confirmed event
   * 2. For SportsPay: Submit token and confirm via API
   * 3. For in-person: Confirm submission via API
   * @returns {Promise<boolean>} True if payment was successful, false otherwise
   */
  const handlePaymentSuccess = async ({ sportsPayToken }) => {
    try {
      // If this is an in-person payment, skip payment processing
      if (alreadyPaidInPerson) {
        try {
          setSubmittingAlreadyInPersonPayment(true)
          setIsInSuccessDelay(true)
          await updateSessionConfirmSubmission(alreadyPaidInPerson)
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setSubmittingAlreadyInPersonPayment(false)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setSubmittingAlreadyInPersonPayment(false)
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // If this is SportsPay, use token-based submission
      if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
        try {
          setIsInSuccessDelay(true)
          await updateSessionConfirmSportsPaySubmission(sportsPayToken)
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // For Stripe payments, wait for WebSocket confirmation
      // The backend will send payment-confirmed or confirmation-failed event
      console.log(
        '🔄 New_RegistrationPayment: Stripe payment successful, waiting for WebSocket confirmation'
      )
      setIsInSuccessDelay(true)
      startWaitingForConfirmation()
      return true
    } catch (sessionError) {
      // Handle session errors
      if (sessionError?.status === 410) {
        setLocalError('Session Has Expired. Please Try Again.')
        await new Promise((resolve) => setTimeout(resolve, 1500))
        deleteSession()
        window.location.reload()
      } else {
        const errorMessage = getErrorMessage(sessionError)
        setLocalError(errorMessage)
      }

      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      return false
    }
  }

  // Handle payment errors
  const handlePaymentError = useCallback(
    (err) => {
      console.error('Payment error:', err)

      // Get a user-friendly error message
      let errorMessage = getErrorMessage(err)

      // Handle specific error types
      if (err?.type === 'card_error') {
        // Card errors are user-facing errors that occur during payment processing
        errorMessage =
          err.message ||
          'Your card was declined. Please try another payment method.'
      } else if (err?.type === 'validation_error') {
        // Validation errors occur when the card details are invalid
        errorMessage =
          err.message || 'Please check your card details and try again.'
      } else if (
        err?.type === 'api_error' ||
        err?.type === 'api_connection_error'
      ) {
        // API errors are usually temporary issues with the payment service
        errorMessage =
          'We are experiencing issues with our payment service. Please try again later.'
      } else {
        // For any other errors, provide a generic message
        errorMessage =
          errorMessage ||
          'An error occurred while processing your payment. Please try again.'
      }

      // Set the error message to display to the user
      setLocalError(errorMessage)
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      // Log the error to Sentry for tracking
      registerError(err, 'Payment processing error', {
        session,
        division,
        couponInfo,
        teamOfficialType,
        role,
      })
    },
    [session, division, couponInfo, teamOfficialType, role]
  )

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) {
      return null
    }

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // Initialize payment provider hook
  const {
    paymentStatus,
    setPaymentStatus,
    isLoading,
    error: paymentError,
    processPayment,
    resetPayment,
    mountStripeElement,
    validateSportsPayCard,
    isInitialized,
  } = usePaymentProvider({
    // With nothing owed there is no intent/secret — a null provider stops the
    // hook from initializing and erroring on the missing secret
    provider: hasNoBalanceDue ? null : ACTIVE_PAYMENT_PROVIDER,
    clientSecret: client_secret,
    sportsPayApiKey: client_secret,
    connectAccount: connected_account_id,
    sportsPayHost: host_name,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentError: handlePaymentError,
    sessionData: {
      session,
      division,
      couponInfo,
      teamOfficialType,
      role,
    },
  })

  // Handle WebSocket payment confirmation state changes (Stripe only)
  useEffect(() => {
    if (
      confirmationState === CONFIRMATION_STATE.CONFIRMED &&
      confirmedSession
    ) {
      console.log(
        '✅ New_RegistrationPayment: WebSocket confirmed, updating session and navigating'
      )
      setSession(confirmedSession)
      setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
      setIsInSuccessDelay(false)
    } else if (
      confirmationState === CONFIRMATION_STATE.FAILED ||
      confirmationState === CONFIRMATION_STATE.TIMEOUT
    ) {
      console.log(
        '❌ New_RegistrationPayment: WebSocket confirmation failed:',
        wsErrorMessage
      )
      setPaymentStatus(PAYMENT_STATUS.FAILED)
      setLocalError(
        wsErrorMessage || 'Payment confirmation failed. Please try again.'
      )
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)
    }
  }, [
    confirmationState,
    confirmedSession,
    wsErrorMessage,
    setSession,
    setStep,
    setPaymentStatus,
  ])

  // Set initial payment method based on deferred payment availability
  useEffect(() => {
    const isDeferredPaymentEnabled = isTeamRep
      ? division?.defer_payment
      : division?.player_defer_payment

    // Only set initial payment method if it's not already set
    if (paymentMethod === null && isDeferredPaymentEnabled === false) {
      setPaymentMethod('credit-card')
    }
  }, [
    isTeamRep,
    division?.defer_payment,
    division?.player_defer_payment,
    paymentMethod,
  ])

  // Sync alreadyPaidInPerson with paymentMethod
  useEffect(() => {
    setAlreadyPaidInPerson(paymentMethod === 'paid-in-person')
  }, [paymentMethod])

  // Set error if payment information is missing
  useEffect(() => {
    // No balance due (e.g. zero-total plan/coupon combination): the secret is
    // intentionally empty — not an error
    if (hasNoBalanceDue) {
      return
    }
    if (
      ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE &&
      (!client_secret || !connected_account_id)
    ) {
      setLocalError(
        !client_secret
          ? 'Payment information is missing. Please try again or contact support.'
          : 'Payment processing account is missing. Please try again or contact support.'
      )
    }
  }, [
    client_secret,
    connected_account_id,
    hasNoBalanceDue,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Mount Stripe element when form is ready
  useEffect(() => {
    // Only mount the element if using Stripe
    if (ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.STRIPE) {
      return
    }

    // Only mount when credit card is selected
    if (paymentMethod !== 'credit-card') {
      return
    }

    // Only mount the element once
    if (elementMounted.current) {
      return
    }
    if (!isInitialized) {
      return
    }
    const paymentDiv = document.querySelector('#payment-element')
    if (paymentDiv && client_secret && connected_account_id) {
      mountStripeElement('payment-element')
      elementMounted.current = true
    }
  }, [
    client_secret,
    connected_account_id,
    mountStripeElement,
    isInitialized,
    paymentMethod,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Validate SportsPay card data when it changes
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.SPORTS_PAY ||
      !validateSportsPayCard ||
      !isInitialized
    ) {
      return
    }

    // If there are errors, don't validate
    if (
      creditCardValues?.errors?.cardNumber ||
      creditCardValues?.errors?.expiryDate ||
      creditCardValues?.errors?.cvc
    ) {
      return
    }

    // Only validate if we have enough data
    if (
      creditCardValues.cardNumber &&
      creditCardValues.expiryDate &&
      creditCardValues.cvc &&
      creditCardValues.cvc.length > 3
    ) {
      const cardData = formatSportsPayCardData()
      if (cardData) {
        const validationResult = validateSportsPayCard(cardData)
        if (!validationResult.datavalid) {
          if (!validationResult.cardnum.isvalid) {
            if (validationResult.cardnum.message) {
              setLocalError(validationResult.cardnum.message)
            }
          }
          if (!validationResult.cardexp.isvalid) {
            if (validationResult.cardexp.message) {
              setLocalError(validationResult.cardexp.message)
            }
          }
          if (!validationResult.cardcvv.isvalid) {
            if (validationResult.cardcvv.message) {
              setLocalError(validationResult.cardcvv.message)
            }
          }
          return
        }
      }
    }
  }, [
    creditCardValues,
    validateSportsPayCard,
    isInitialized,
    formatSportsPayCardData,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    // Clear any previous errors before processing payment
    setLocalError(null)
    setHasSubmittedPayment(true)

    // Nothing owed: bypass payment-method selection, card collection and the
    // deferred path entirely — confirm straight to the no-payment-due state
    if (hasNoBalanceDue) {
      try {
        setIsInSuccessDelay(true)
        await updateSessionConfirmSubmission()
        setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
        setIsInSuccessDelay(false)
      } catch (submissionError) {
        setIsInSuccessDelay(false)
        setHasSubmittedPayment(false)
        handlePaymentError(submissionError)
      }
      return
    }

    // Validate that a payment method is selected
    if (!paymentMethod) {
      setLocalError('Please select a payment method')
      setHasSubmittedPayment(false)
      return
    }

    // If already paid in person, skip payment processing
    if (alreadyPaidInPerson) {
      await handlePaymentSuccess({})
      return
    }

    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      await processPayment(cardData)
    } else {
      // Stripe payment processing
      await processPayment()
    }
  }

  // Handle credit card input changes
  const handleCreditCardChange = useCallback((values) => {
    setCreditCardValues(values)
  }, [])

  // Reset payment provider and elements (used when unchecking paid in person checkbox)
  const handlePaymentReset = () => {
    resetPayment()
    resetConfirmation()
    // Reset the element mounted flag so it can be mounted again
    elementMounted.current = false
    // Clear any error messages
    setLocalError(null)

    // Force remount of Stripe element by adding a small delay
    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE) {
      setTimeout(() => {
        const paymentDiv = document.querySelector('#payment-element')
        if (
          paymentDiv &&
          client_secret &&
          connected_account_id &&
          isInitialized
        ) {
          mountStripeElement('payment-element')
          elementMounted.current = true
        }
      }, 100)
    }
  }

  /**
   * Switches the payment plan: the server re-prices the session and issues a
   * fresh payment intent, so the mounted payment element must be torn down and
   * remounted against the new client_secret (the mount effect re-runs on it).
   */
  const handlePaymentPlanChange = async (plan) => {
    if (plan === selectedPaymentPlan || isRepricing) {
      return
    }
    setIsRepricing(true)
    setLocalError(null)
    try {
      await updateSessionPayment(plan)
      resetPayment()
      resetConfirmation()
      elementMounted.current = false
    } catch (err) {
      handlePaymentError(err)
    } finally {
      setIsRepricing(false)
    }
  }

  // Shared back navigation: players with a required waiver step back to the
  // waiver, everyone else steps back to team registration.
  const handleBack = useCallback(() => {
    if (waiverRequired && !isTeamRep) {
      setStep(REGISTRATION_STEPS.WAIVER)
    } else {
      setStep(REGISTRATION_STEPS.TEAM_REGISTER)
    }
  }, [waiverRequired, isTeamRep, setStep])

  // Determine which error to display (local or from payment provider)
  const displayError = localError || paymentError

  // Combine all relevant loading/processing states to prevent session loss actions
  const isPaymentProcessing = useMemo(() => {
    return (
      hasSubmittedPayment &&
      (paymentStatus === PAYMENT_STATUS.PROCESSING ||
        isInSuccessDelay ||
        submittingAlreadyInPersonPayment ||
        isWaitingForWsConfirmation)
    )
  }, [
    hasSubmittedPayment,
    paymentStatus,
    isInSuccessDelay,
    submittingAlreadyInPersonPayment,
    isWaitingForWsConfirmation,
  ])

  // Warn user when attempting to close/refresh tab during payment processing
  useEffect(() => {
    if (!isPaymentProcessing) {
      return
    }

    const beforeUnloadHandler = (e) => {
      // Modern browsers ignore custom text; setting returnValue triggers the prompt.
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [isPaymentProcessing])

  // UI fragments for each payment method. Plain derivations on purpose: they
  // capture handleSubmit, whose identity changes every render, so a useMemo
  // here would recompute every render anyway (and trips exhaustive-deps).
  const creditCardUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ||
    !ACTIVE_PAYMENT_PROVIDER ? null : (
      <>
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY && (
          <CreditCardInput
            onChange={handleCreditCardChange}
            countries={countries}
            disabled={alreadyPaidInPerson}
          />
        )}
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE && (
          <div id='payment-element' />
        )}
        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
            margin-top: 16px;
          `}
        >
          <Button
            onClick={handleBack}
            text='BACK'
            variant='secondary'
            disabled={isLoading || isRepricing}
          />
          <Button
            onClick={handleSubmit}
            text='PAY NOW'
            variant='primary'
            disabled={isLoading || isRepricing}
            isLoading={isLoading}
          />
        </div>
      </>
    )

  /**
   * Rendered INSTEAD of the payment-method selector when nothing is owed
   * (e.g. a 100% coupon on the selected plan): no method choice, no card form,
   * no deferred path — submission confirms the registration directly.
   */
  const zeroBalanceUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ? null : (
      <div
        css={css`
          display: flex;
          padding: 16px;
          background: ${colors.NEUTRAL_600};
          border-radius: 8px;
          border: 1px solid ${colors.NEUTRAL_400};
          flex-direction: column;
          gap: 16px;
          width: 100%;
        `}
      >
        <Text variant='paragraphMedium' color={colors.WHITE} weight='bold'>
          Payment
        </Text>
        <div
          css={css`
            align-self: stretch;
            height: 0;
            border: 0.5px solid ${colors.NEUTRAL_400};
          `}
        />
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          No payment is due today — your discount covers the full amount owed.
          Complete your registration below.
        </Text>
        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
          `}
        >
          <Button
            onClick={handleBack}
            text='BACK'
            variant='secondary'
            disabled={isRepricing || isInSuccessDelay}
          />
          <Button
            onClick={handleSubmit}
            text='COMPLETE REGISTRATION'
            variant='primary'
            disabled={isRepricing || isInSuccessDelay}
            isLoading={isInSuccessDelay}
          />
        </div>
      </div>
    )

  const paidInPersonUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ? null : (
      <>
        <Button
          onClick={handleBack}
          text='BACK'
          variant='secondary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
        />
        <Button
          onClick={handleSubmit}
          text='SUBMIT REGISTRATION'
          variant='primary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
          isLoading={isLoading || submittingAlreadyInPersonPayment}
        />
      </>
    )

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: center;
          background-color: ${colors.NEUTRAL_800};
          width: 1080px;
          padding: 0 35px 35px 35px;
          border-radius: 16px 16px 0px 0px;
          @media (max-width: 768px) {
            min-width: 0;
            width: 100%;
          }
        `}
      >
        <LoggedUserBar
          email={email}
          disabled={isPaymentProcessing}
          onStartOver={() => {
            resetSession()
            setTimeout(() => {
              window.location.reload()
            }, 500)
          }}
        />
        <EventCard eventInfo={registration} />
        <EventProgress
          eventProgress={5}
          isWaiverRequired={waiverRequired}
          isTeamRep={isTeamRep}
        />
        <PageTitle
          title={
            isSinglePayment || isPayInFullSelected
              ? 'Pay Your Registration'
              : 'Pay Your Deposit'
          }
          subtitle={
            isSinglePayment || isPayInFullSelected
              ? 'Please make a payment to complete your registration for the competition.'
              : 'Please make a deposit payment to complete your registration for the competition.'
          }
          showAlert={true}
          showSuccess={false}
        />
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
            @media (max-width: 768px) {
              flex-direction: column;
              gap: 0px;
              align-items: center;
              max-height: unset;
            }
            ${ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY &&
            `
              max-height: unset;
            `}
          `}
        >
          {/* Payment Info - SportNinja Data */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              /* Tight row rhythm so the line items read as one receipt block.
                 The dividers and the deposit block carry their own margins, so
                 this only affects the spacing between amount rows. */
              gap: 2px;
              flex: 1 0 0;
              @media (max-width: 768px) {
                margin-bottom: 16px;
                width: 100%;
              }
              background-color: ${colors.NEUTRAL_600};
              border-radius: 8px;
              border: 1px solid ${colors.NEUTRAL_400};
              padding: 16px;
            `}
          >
            <p
              css={css`
                ${paragraphMediumBoldStyle}
                color: ${colors.WHITE};
                align-self: stretch;
              `}
            >
              Payment Info
            </p>
            <div
              css={css`
                align-self: stretch;
                height: 0;
                margin: 4px 0 8px;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            {isPayInFullSelected && paymentPlanOptions ? (
              // Pay in Full breakdown (Figma 19521:12341): Deposit Fee + the
              // remainder labelled "Registration Fee"; together they sum to Subtotal
              <>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${receiptLineStyle}
                      flex: 1 0 0;
                    `}
                  >
                    Deposit Fee
                  </p>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                  >
                    {formatCurrency(
                      paymentPlanOptions.depositAmount,
                      currencyCode
                    )}
                  </p>
                </div>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    align-self: stretch;
                    /* Matches the deposit-only branch: separates the purchased
                       items from the totals rows below. */
                    margin-bottom: 20px;
                  `}
                >
                  <p
                    css={css`
                      ${receiptLineStyle}
                      flex: 1 0 0;
                    `}
                  >
                    Registration Fee
                  </p>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                  >
                    {formatCurrency(
                      paymentPlanOptions.fullAmount -
                        paymentPlanOptions.depositAmount,
                      currencyCode
                    )}
                  </p>
                </div>
              </>
            ) : effectiveDeposit && effectiveDeposit > 0 ? (
              <div
                css={css`
                  display: flex;
                  /* flex-start, not center: the label is a two-line stack
                     (Deposit Fee / competition name) and the amount must sit on
                     the same baseline as the first line, not float between. */
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                  /* Separates the purchased item from the totals rows below. */
                  margin-bottom: 20px;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    flex: 1 0 0;
                  `}
                >
                  <p css={receiptLineStyle}>Deposit Fee</p>
                  <p css={receiptSubtitleStyle}>{root_schedule_name}</p>
                </div>
                <p
                  css={css`
                    ${receiptLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {deposit}
                </p>
              </div>
            ) : null}
            {/* Separator */}
            <div
              css={css`
                width: 100%;
                border-color: ${colors.VERY_DARK_GRAYISH_BLUE};
                border-bottom-width: 1px;
                border-bottom-style: solid;
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${receiptLineStyle}
                  flex: 1 0 0;
                `}
              >
                Subtotal
              </p>
              <p
                css={css`
                  ${receiptLineStyle}
                  ${tabularAmountStyle}
                `}
              >
                {formatCurrency(subtotal, currencyCode)}
              </p>
            </div>
            {/* Coupon discount sits directly under Subtotal (it's an adjustment
                to the Subtotal), before the League/processing fee lines. */}
            {couponInfo?.id && isSinglePayment && couponInfo?.is_installment ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                </p>
              </div>
            ) : null}
            {couponInfo?.id && isPayInFullSelected && discount > 0 ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(discount, currencyCode)}
                </p>
              </div>
            ) : null}
            {couponInfo?.id &&
            !isSinglePayment &&
            !isPayInFullSelected &&
            couponInfo?.is_deposit ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -
                  {formatCurrency(
                    couponInfo?.deposit_discount_amount,
                    currencyCode
                  )}
                </p>
              </div>
            ) : null}
            {/* League fees sit above Tax (taxed); payment processing sits below
                Tax (struck on the final amount). Keyed off fee identity, not
                API order — see splitFeeLineItemsForReceipt. */}
            {taxedFees.map((fee) => (
              <div
                key={fee.key}
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  {fee.label}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(fee.amount, currencyCode)}
                </p>
              </div>
            ))}
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${receiptSecondaryLineStyle}
                  flex: 1 0 0;
                `}
              >
                Tax
              </p>
              <p
                css={css`
                  ${receiptSecondaryLineStyle}
                  ${tabularAmountStyle}
                `}
              >
                {formatCurrency(tax, currencyCode)}
              </p>{' '}
            </div>
            {postTaxFees.map((fee) => (
              <div
                key={fee.key}
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  {fee.label}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(fee.amount, currencyCode)}
                </p>
              </div>
            ))}

            {/* Separator */}
            <div
              css={css`
                align-self: stretch;
                height: 0;
                margin: 8px 0 4px;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 22px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Total
              </p>
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  ${tabularAmountStyle}
                `}
              >
                {formatCurrency(total, currencyCode)}
              </p>
            </div>
            {isSinglePayment || isPayInFullSelected ? null : (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  align-self: stretch;
                  /* display none if couponInfo is null */
                  display: ${couponInfo?.id && !couponInfo?.is_deposit
                    ? 'flex'
                    : 'none'};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                      flex: 1 0 0;
                    `}
                  >
                    Coupon Code: {couponInfo?.code}
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                    `}
                  >
                    -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                  </p>
                </div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  * Discount will be applied to the remaining balance.
                </Text>
              </div>
            )}
          </div>
          {/* Separator Between SportNinja and Stripe Stuff */}
          <div
            css={css`
              height: 100%;
              width: 1px;
              background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
            `}
          />
          {/* Payment Provider UI */}
          <div
            css={css`
              width: 50%;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              flex-direction: column;
              @media (max-width: 768px) {
                width: 100%;
                padding-top: 0px;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              {paymentStatus === PAYMENT_STATUS.SUCCESS && !displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 250px;
                  `}
                >
                  <Icon name='check' color={colors.PRIMARY} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                    `}
                  >
                    {isWaitingForWsConfirmation
                      ? 'Payment Successful.\nConfirming your registration...'
                      : 'Payment Successful.\nYou will be redirected shortly.'}
                  </p>
                  <Spin
                    css={css`
                      margin-top: 32px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              ) : paymentStatus === PAYMENT_STATUS.FAILED || displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 300px;
                    width: 100%;
                    overflow: hidden;
                  `}
                >
                  <Icon name='times' color={colors.ERROR_LIGHT} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                      width: 100%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    Payment Failed
                  </p>
                  <span
                    css={css`
                      ${paragraphSmallBoldStyle}
                      color: ${colors.WHITE};
                      margin-top: 16px;
                      margin-bottom: 16px;
                      text-align: center;
                      max-width: 90%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {displayError || 'Something went wrong'}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      gap: 16px;
                      margin-top: 16px;
                      @media (max-width: 768px) {
                        flex-direction: column-reverse;
                        width: 100%;
                        gap: 8px;
                      }
                    `}
                  >
                    <button
                      type='button'
                      disabled={isLoading}
                      onClick={() => {
                        resetPayment()
                        resetConfirmation()
                        elementMounted.current = false
                        setLocalError(null)

                        if (
                          ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE
                        ) {
                          setTimeout(() => {
                            const paymentDiv =
                              document.querySelector('#payment-element')
                            if (
                              paymentDiv &&
                              client_secret &&
                              connected_account_id &&
                              isInitialized
                            ) {
                              mountStripeElement('payment-element')
                              elementMounted.current = true
                            }
                          }, 100)
                        }
                      }}
                      css={css`
                        ${alternateButtonStyle}
                        ${headingSixStyle}
                        width: 100%;
                        max-height: 40px;
                        cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                      `}
                    >
                      BACK
                    </button>
                  </div>
                </div>
              ) : (
                <form id='payment-form' onSubmit={handleSubmit}>
                  {paymentPlanOptions ? (
                    <PaymentPlanSelector
                      options={paymentPlanOptions}
                      selectedPlan={selectedPaymentPlan}
                      onChange={handlePaymentPlanChange}
                      disabled={
                        isRepricing ||
                        isLoading ||
                        isPaymentProcessing ||
                        submittingAlreadyInPersonPayment
                      }
                      formatAmount={(amount) =>
                        formatCurrency(amount, currencyCode)
                      }
                    />
                  ) : null}
                  {hasNoBalanceDue ? (
                    zeroBalanceUI
                  ) : (
                    <PaymentMethodSelector
                      isDeferredPaymentEnabled={
                        isTeamRep
                          ? division?.defer_payment
                          : division?.player_defer_payment
                      }
                      selectedMethod={paymentMethod}
                      onChange={(method) => {
                        if (
                          paymentMethod === 'paid-in-person' &&
                          method === 'credit-card'
                        ) {
                          handlePaymentReset()
                        }
                        // Reset element mounted flag when changing payment methods
                        if (paymentMethod !== method) {
                          elementMounted.current = false
                        }
                        setPaymentMethod(method)
                      }}
                      disabled={isLoading || submittingAlreadyInPersonPayment}
                      isPaymentProviderNotSet={!ACTIVE_PAYMENT_PROVIDER}
                      onBack={handleBack}
                      backDisabled={
                        isLoading || submittingAlreadyInPersonPayment
                      }
                    >
                      {paymentMethod === 'credit-card'
                        ? creditCardUI
                        : paymentMethod === 'paid-in-person'
                        ? paidInPersonUI
                        : null}
                    </PaymentMethodSelector>
                  )}
                  {/* Show back button when no payment method is selected and defer payment is enabled */}
                  {!hasNoBalanceDue &&
                    paymentMethod === null &&
                    (isTeamRep
                      ? division?.defer_payment
                      : division?.player_defer_payment) && (
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-end;
                          width: 100%;
                          margin-top: 16px;
                        `}
                      >
                        <Button
                          onClick={handleBack}
                          text='BACK'
                          variant='secondary'
                          disabled={
                            isLoading || submittingAlreadyInPersonPayment
                          }
                          css={css`
                            width: 50%;
                            max-width: 200px;
                            @media (max-width: 768px) {
                              width: 100%;
                              max-width: 100%;
                            }
                          `}
                        />
                      </div>
                    )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RegistrationPayment
